import trackingConfig from '@/config/tracking';

const META_PIXEL_SCRIPT_ID = 'meta-pixel-script';
const META_PIXEL_SCRIPT_SRC = 'https://connect.facebook.net/en_US/fbevents.js';
const META_PIXEL_STORAGE_KEY = 'meta_pixel_resolved_id';
const DEFAULT_STORE_NICK = process.env.VUE_APP_STORE_NICK || 'psiqueasy';

const DIRECT_PIXEL_KEYS = [
  'meta_pixel_id',
  'metaPixelId',
  'facebook_pixel_id',
  'facebookPixelId',
  'pixel_id',
  'pixelId',
];

const LIKELY_PIXEL_CONTEXT_REGEX = /(pixel|facebook|meta|tracking|analytics|marketing)/i;

const initializedPixelIds = new Set();
let bootPromise = null;
let lastTrackedPath = null;
let pendingPageViewPath = null;
let warnedMissingPixel = false;

const isBrowser = () => typeof window !== 'undefined' && typeof document !== 'undefined';

const normalizeString = value => {
  if (value === null || value === undefined) return null;
  const normalized = String(value).trim();
  return normalized.length ? normalized : null;
};

const normalizeRoutePath = value => {
  const normalized = normalizeString(value);
  if (!normalized) return null;
  return normalized.split('#')[0] || normalized;
};

const getCurrentPath = () => {
  if (!isBrowser()) return null;
  return normalizeRoutePath(`${window.location.pathname || ''}${window.location.search || ''}`);
};

const getRoutePath = route => normalizeRoutePath(route?.fullPath || route?.path) || getCurrentPath();

const pickFirstString = (values = []) => values
  .map(normalizeString)
  .find(Boolean) || null;

const persistPixelId = value => {
  try {
    if (!isBrowser() || typeof sessionStorage === 'undefined') return;
    if (!value) {
      sessionStorage.removeItem(META_PIXEL_STORAGE_KEY);
      return;
    }
    sessionStorage.setItem(META_PIXEL_STORAGE_KEY, String(value));
  } catch (err) {
    // ignore storage errors
  }
};

const getStoredPixelId = () => {
  try {
    if (!isBrowser() || typeof sessionStorage === 'undefined') return null;
    return normalizeString(sessionStorage.getItem(META_PIXEL_STORAGE_KEY));
  } catch (err) {
    return null;
  }
};

const extractPixelIdFromObject = (input, depth = 0, parentKey = '') => {
  if (!input || depth > 6) return null;

  const currentContextMatches = LIKELY_PIXEL_CONTEXT_REGEX.test(parentKey || '');

  if (typeof input === 'string' || typeof input === 'number') {
    return currentContextMatches ? normalizeString(input) : null;
  }

  if (Array.isArray(input)) {
    return input
      .map(item => extractPixelIdFromObject(item, depth + 1, parentKey))
      .find(Boolean) || null;
  }

  if (typeof input !== 'object') return null;

  const directKeys = currentContextMatches ? [...DIRECT_PIXEL_KEYS, 'id'] : DIRECT_PIXEL_KEYS;
  const directMatch = pickFirstString(directKeys.map(key => input[key]));
  if (directMatch) return directMatch;

  const prioritizedKeys = Object.keys(input).sort((a, b) => {
    const aMatch = LIKELY_PIXEL_CONTEXT_REGEX.test(a) ? 0 : 1;
    const bMatch = LIKELY_PIXEL_CONTEXT_REGEX.test(b) ? 0 : 1;
    return aMatch - bMatch;
  });

  return prioritizedKeys
    .map(key => extractPixelIdFromObject(input[key], depth + 1, key))
    .find(Boolean) || null;
};

const resolvePixelIdFromRuntime = () => {
  if (!isBrowser()) return normalizeString(process.env.VUE_APP_META_PIXEL_ID);

  return pickFirstString([
    Reflect.get(window, '__META_PIXEL_ID__'),
    trackingConfig?.metaPixelId,
    process.env.VUE_APP_META_PIXEL_ID,
    getStoredPixelId(),
  ]);
};

const ensureMetaPixelBootstrap = () => {
  if (!isBrowser()) return;

  if (!window.fbq) {
    const queuedFbq = (...args) => {
      if (queuedFbq.callMethod) {
        queuedFbq.callMethod(...args);
        return;
      }

      queuedFbq.queue.push(args);
    };

    queuedFbq.queue = [];
    queuedFbq.loaded = true;
    queuedFbq.version = '2.0';
    queuedFbq.push = queuedFbq;

    window.fbq = queuedFbq;
    Reflect.set(window, '_fbq', queuedFbq);
  }

  if (document.getElementById(META_PIXEL_SCRIPT_ID)) return;

  const script = document.createElement('script');
  script.id = META_PIXEL_SCRIPT_ID;
  script.async = true;
  script.src = META_PIXEL_SCRIPT_SRC;

  const firstScript = document.getElementsByTagName('script')[0];
  if (firstScript?.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript);
    return;
  }

  document.head.appendChild(script);
};

const initMetaPixel = pixelId => {
  const normalizedPixelId = normalizeString(pixelId);
  if (!normalizedPixelId || !isBrowser()) return null;

  ensureMetaPixelBootstrap();

  if (!initializedPixelIds.has(normalizedPixelId)) {
    window.fbq('init', normalizedPixelId);
    initializedPixelIds.add(normalizedPixelId);
  }

  warnedMissingPixel = false;
  persistPixelId(normalizedPixelId);
  return normalizedPixelId;
};

const fetchPublicPayload = async (apiClient, url) => {
  if (!apiClient || typeof apiClient.get !== 'function') return null;

  try {
    const response = await apiClient.get(url);
    return response?.data ?? response ?? null;
  } catch (err) {
    return null;
  }
};

const resolvePixelIdFromApi = async ({ apiClient, storeNick = DEFAULT_STORE_NICK } = {}) => {
  const normalizedStoreNick = normalizeString(storeNick) || DEFAULT_STORE_NICK;
  const encodedStoreNick = encodeURIComponent(normalizedStoreNick);

  const storeInfo = await fetchPublicPayload(apiClient, `/customer/stores/${encodedStoreNick}/info`);
  const pixelIdFromStoreInfo = extractPixelIdFromObject(storeInfo);
  if (pixelIdFromStoreInfo) return pixelIdFromStoreInfo;

  const checkoutConfig = await fetchPublicPayload(apiClient, `/customer/store/${encodedStoreNick}/checkout/store/config`);
  return extractPixelIdFromObject(checkoutConfig);
};

const sendMetaPageView = route => {
  const currentPath = getRoutePath(route);
  if (!currentPath) return false;

  pendingPageViewPath = currentPath;

  if (!isBrowser() || initializedPixelIds.size === 0 || typeof window.fbq !== 'function') {
    return false;
  }

  if (lastTrackedPath === currentPath) {
    pendingPageViewPath = null;
    return true;
  }

  window.fbq('track', 'PageView');
  lastTrackedPath = currentPath;
  pendingPageViewPath = null;
  return true;
};

export const setupMetaPixel = ({ apiClient, storeNick = DEFAULT_STORE_NICK, initialRoute = null } = {}) => {
  if (!isBrowser()) return Promise.resolve(null);

  const initialPath = getRoutePath(initialRoute);
  if (initialPath) pendingPageViewPath = initialPath;

  if (bootPromise) return bootPromise;

  bootPromise = (async () => {
    const runtimePixelId = resolvePixelIdFromRuntime();
    const apiPixelId = runtimePixelId ? null : await resolvePixelIdFromApi({ apiClient, storeNick });
    const resolvedPixelId = initMetaPixel(runtimePixelId || apiPixelId);

    if (!resolvedPixelId) {
      if (!warnedMissingPixel) {
        console.info('[meta-pixel] Nenhum Pixel ID foi encontrado na aplicação ou nos endpoints públicos da loja.');
        warnedMissingPixel = true;
      }
      return null;
    }

    if (pendingPageViewPath) {
      sendMetaPageView({ fullPath: pendingPageViewPath });
    }
    return resolvedPixelId;
  })().catch(err => {
    console.warn('[meta-pixel]', err?.message || err);
    return null;
  });

  return bootPromise;
};

export const trackMetaPageView = sendMetaPageView;

export function trackMetaEvent(eventName, params = {}) {
  const normalizedEventName = normalizeString(eventName);
  if (!normalizedEventName || !isBrowser() || initializedPixelIds.size === 0 || typeof window.fbq !== 'function') {
    return false;
  }

  window.fbq('track', normalizedEventName, params || {});
  return true;
}
